import { useState } from "react"

import { Link } from "react-router-dom"

import { batteryConcertErrorCode as concertErrorCode } from "~/api/battery-concert-application-errors"
import type { SolarConcertCustomerContextDto } from "~/api/dto/solar-concert.dto"
import type { SolarProposalFinancingDto } from "~/api/dto/solar-proposal-financing.dto"
import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"
import { invalidateSolarConcertDependents } from "~/api/hooks/solar-concert-application-cache"
import useInitiateSolarConcertApplication from "~/api/hooks/useInitiateSolarConcertApplication"
import useSolarConcertApplication from "~/api/hooks/useSolarConcertApplication"
import useSolarConcertSellerReadiness from "~/api/hooks/useSolarConcertSellerReadiness"
import useSolarProposal from "~/api/hooks/useSolarProposal"
import { isSolarRefreshPricingRequiredError } from "~/api/solar-proposal-errors"

import {
  solarConcertApplicationBundle,
  solarConcertCommandErrorMessage,
} from "./solar-concert-bundle"
import { solarSetupStepPath } from "./solar-setup-paths"
import { SolarConcertSellerBanner } from "./SolarConcertSellerBanner"
import { SolarRefreshPricingFix } from "./SolarRefreshPricingFix"
import {
  ConcertApplicationForm,
  type ConcertApplicantPrefill,
} from "../ProposalSetup/financing/concert/ConcertApplicationForm"
import { ConcertApplicationStatus } from "../ProposalSetup/financing/concert/ConcertApplicationStatus"
import { useConcertApplicationNotifications } from "../ProposalSetup/financing/concert/use-concert-application-notifications"
import { APPLY_FOR_FINANCING_LABEL } from "../ProposalSetup/financing/credit-human-copy"
import { btnPrimary } from "../ProposalSetup/financing/financing-chrome"

export const CONCERT_OFFER_REQUIRED_PROMPT = `Choose a Concert offer on the Loan Product step before applying.`
export const OPEN_LOAN_PRODUCT_LABEL = `Open Loan Product`
export const CONCERT_QUOTE_NOT_CURRENT_PROMPT = `Refresh Pricing first: the Concert Quote no longer matches this proposal.`
export const SETTING_UP_CUSTOMER_LABEL = `Setting up customer`

type FormContext = { quoteId: string; prefill: ConcertApplicantPrefill }

const prefillFromHeader = (
  header: SolarProposalDto | undefined,
): ConcertApplicantPrefill => ({
  firstName: header?.homeowner?.firstName ?? ``,
  lastName: header?.homeowner?.lastName ?? ``,
  email: header?.homeowner?.email ?? ``,
  phone: header?.homeowner?.phone ?? ``,
  street: header?.address.street ?? ``,
  city: header?.address.city ?? ``,
  state: header?.address.state ?? ``,
  zip: header?.address.postal ?? ``,
})

/** What Initiate confirmed, falling back to the proposal's own facts. */
const prefillFromContext = (
  context: SolarConcertCustomerContextDto,
  fallback: ConcertApplicantPrefill,
): ConcertApplicantPrefill => ({
  firstName: context.customer.cfirstname || fallback.firstName,
  lastName: context.customer.clastname || fallback.lastName,
  email: context.customer.cemail ?? fallback.email,
  phone: context.customer.cphone1 ?? fallback.phone,
  street: context.customer.caddress1 || fallback.street,
  city: context.customer.ccity || fallback.city,
  state: context.customer.cstate || fallback.state,
  zip: context.customer.czip || fallback.zip,
})

/**
 * Concert's financing status on a Solar Proposal. Before an application:
 * the seller's Concert account gate and the Quote's freshness, each with its
 * fix inline, then Apply, which creates or reuses the Concert customer and
 * opens the in-person form (with an optional co-applicant). After: the shared
 * Concert status, stipulations and loan documents. Gateway notifications
 * refresh every read.
 */
export const SolarConcertFinancingStatus = ({
  proposalId,
  financing,
}: {
  proposalId: string
  financing: SolarProposalFinancingDto
}): JSX.Element => {
  useConcertApplicationNotifications(
    proposalId,
    invalidateSolarConcertDependents,
  )
  const statusQuery = useSolarConcertApplication(proposalId)
  const readinessQuery = useSolarConcertSellerReadiness(proposalId)
  const header = useSolarProposal(proposalId).data
  const initiate = useInitiateSolarConcertApplication()
  const [form, setForm] = useState<FormContext | null>(null)

  const status = statusQuery.data ?? null
  const application = status?.application ?? null
  const quoteId = financing.quote?.id ?? null
  const offerMissing = financing.selectedLoanProduct == null
  const quoteNotCurrent =
    !offerMissing &&
    (financing.refreshPricingRequired || financing.quoteStatus !== `current`)
  const sellerReady = readinessQuery.data?.status === `ready`
  const canInitiate =
    status?.actions.initiate.allowed === true && quoteId != null
  const pending = initiate.isLoading
  // The account gate and the Quote fix explain themselves; any other refusal
  // shows the server's reason.
  const initiateReason =
    status == null || canInitiate || offerMissing || quoteNotCurrent
      ? null
      : sellerReady
        ? status.actions.initiate.reason
        : null

  const onApply = (): void => {
    if (!canInitiate || pending || quoteId == null) return
    initiate.mutate(
      { proposalId, body: { quoteId } },
      {
        onSuccess: (context) => {
          setForm({
            quoteId: context.quoteId,
            prefill: prefillFromContext(context, prefillFromHeader(header)),
          })
        },
      },
    )
  }

  const primary =
    application?.applicants.find((applicant) => applicant.isPrimary) ?? null
  const onRetry =
    quoteId != null
      ? () => {
          const fallback = prefillFromHeader(header)
          setForm({
            quoteId,
            prefill: {
              ...fallback,
              firstName: primary?.firstName ?? fallback.firstName,
              lastName: primary?.lastName ?? fallback.lastName,
              email: primary?.email ?? fallback.email,
              phone: primary?.phone ?? fallback.phone,
            },
          })
        }
      : undefined

  const initiateError =
    initiate.isError &&
    concertErrorCode(initiate.error) !== `CONCERT_APPLICATION_EXISTS`
      ? initiate.error
      : null

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="solar-concert-financing-status"
    >
      {application == null ? (
        <>
          <SolarConcertSellerBanner proposalId={proposalId} />
          {offerMissing ? (
            <div
              role="status"
              data-testid="solar-concert-offer-required"
              className="flex flex-col items-start gap-2"
            >
              <p className="text-[length:var(--step--2)] text-bp-gold">
                {CONCERT_OFFER_REQUIRED_PROMPT}
              </p>
              <Link
                to={solarSetupStepPath(proposalId, `loan-product`)}
                className="text-[length:var(--step--2)] font-semibold text-bp-text underline underline-offset-2"
              >
                {OPEN_LOAN_PRODUCT_LABEL}
              </Link>
            </div>
          ) : quoteNotCurrent ? (
            <div
              role="status"
              data-testid="solar-concert-refresh-required"
              className="flex flex-col items-start gap-2"
            >
              <p className="text-[length:var(--step--2)] text-bp-gold">
                {CONCERT_QUOTE_NOT_CURRENT_PROMPT}
              </p>
              <SolarRefreshPricingFix
                proposalId={proposalId}
                onRefreshed={() => undefined}
              />
            </div>
          ) : null}
          {initiateError != null ? (
            <div
              role="alert"
              data-testid="solar-concert-initiate-error"
              className="flex flex-col items-start gap-2 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2 text-[length:var(--step--2)] text-bp-danger"
            >
              <span>{solarConcertCommandErrorMessage(initiateError)}</span>
              {isSolarRefreshPricingRequiredError(initiateError) ? (
                <SolarRefreshPricingFix
                  proposalId={proposalId}
                  onRefreshed={() => initiate.reset()}
                />
              ) : null}
            </div>
          ) : null}
          {status != null ? (
            <div className="flex flex-col items-start gap-2">
              <button
                type="button"
                disabled={!canInitiate || pending}
                onClick={onApply}
                className={btnPrimary}
              >
                {pending ? SETTING_UP_CUSTOMER_LABEL : APPLY_FOR_FINANCING_LABEL}
              </button>
              {initiateReason ? (
                <p
                  data-testid="solar-concert-initiate-reason"
                  className="text-[length:var(--step--2)] text-bp-text-muted"
                >
                  {initiateReason}
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
      <ConcertApplicationStatus
        bundle={solarConcertApplicationBundle}
        proposalId={proposalId}
        onRetry={onRetry}
      />
      {form != null ? (
        <ConcertApplicationForm
          bundle={solarConcertApplicationBundle}
          proposalId={proposalId}
          // Refresh Pricing from the form's refusal replaces the Quote; a retry
          // submits the one the financing read now reports.
          quoteId={quoteId ?? form.quoteId}
          prefill={form.prefill}
          onClose={() => setForm(null)}
          onSubmitted={() => setForm(null)}
        />
      ) : null}
    </div>
  )
}
